// src/pages/ReportsPage.js
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Paper, Grid, TextField,
    CircularProgress, Alert, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, Select, MenuItem, InputLabel, FormControl
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import api from '../api/index.js';
import { format } from 'date-fns';

// Helper para formatear moneda, a prueba de valores nulos o inválidos
const formatCurrency = (value) => {
    const number = Number(value);
    if (isNaN(number)) {
        return '$0.00';
    }
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
    }).format(number);
};

const ReportsPage = () => {
    // --- ESTADOS ---
    const [filters, setFilters] = useState({
        startDate: format(new Date(), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
        storeId: '', // '' para 'Todas las tiendas'
    });
    const [stores, setStores] = useState([]);

    // Estados para los datos de cada reporte
    const [cashSessionsReport, setCashSessionsReport] = useState(null);
    const [inventoryValueReport, setInventoryValueReport] = useState(null);
    const [salesDetailsReport, setSalesDetailsReport] = useState(null);
    
    // Estados de carga para cada botón
    const [loading, setLoading] = useState({ cash: false, inventory: false, sales: false });
    const [error, setError] = useState('');
    
    // Cargar la lista de tiendas para el filtro al montar el componente
    useEffect(() => {
        api.get('/stores')
           .then(res => setStores(res.data))
           .catch(err => console.error("No se pudieron cargar las tiendas:", err));
    }, []);

    // --- MANEJADORES ---
    const handleFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const generateCashSessionsReport = async () => {
        setLoading(prev => ({ ...prev, cash: true }));
        setError('');
        try {
            const params = new URLSearchParams(filters).toString();
            const response = await api.get(`/reports/cash-sessions?${params}`);
            setCashSessionsReport(response.data);
        } catch (err) {
            setError('No se pudo generar el reporte de cortes de caja.');
        } finally {
            setLoading(prev => ({ ...prev, cash: false }));
        }
    };

    const generateInventoryValueReport = async () => {
        setLoading(prev => ({ ...prev, inventory: true }));
        setError('');
        try {
            const response = await api.get('/reports/inventory-value');
            setInventoryValueReport(response.data);
        } catch (err) {
            setError('No se pudo generar el reporte de valor de inventario.');
        } finally {
            setLoading(prev => ({ ...prev, inventory: false }));
        }
    };

    const generateSalesDetailsReport = async () => {
        setLoading(prev => ({ ...prev, sales: true }));
        setError('');
        try {
            const params = new URLSearchParams(filters).toString();
            const response = await api.get(`/reports/sales-details?${params}`);
            // --- LOG DE DEPURACIÓN CRÍTICO ---
        console.log("--- DATOS CRUDOS RECIBIDOS DEL BACKEND ---");
        console.log(response.data);
        console.log("----------------------------------------");
        
            
            setSalesDetailsReport(response.data);
        } catch (err) {
            setError('No se pudo generar el reporte de ventas detallado.');
        } finally {
            setLoading(prev => ({ ...prev, sales: false }));
        }
    };
    
    // --- DEFINICIONES DE COLUMNAS PARA LAS TABLAS ---
    const cashSessionsColumns = [
        { field: 'id', headerName: 'ID Sesión', width: 100 },
        { field: 'cashier_name', headerName: 'Cajero', width: 200 },
        { field: 'store_name', headerName: 'Tienda', width: 180 },
        { field: 'opened_at', headerName: 'Apertura', width: 180, type: 'dateTime', valueGetter: (value) => new Date(value) },
        { field: 'closed_at', headerName: 'Cierre', width: 180, type: 'dateTime', valueGetter: (value) => new Date(value) },
        { field: 'opening_balance', headerName: 'Saldo Inicial', type: 'number', width: 130, valueFormatter: ({ value }) => formatCurrency(value) },
        { field: 'closing_balance', headerName: 'Contado Final', type: 'number', width: 130, valueFormatter: ({ value }) => formatCurrency(value) },
        { field: 'expected_balance', headerName: 'Saldo Esperado', type: 'number', width: 140, valueFormatter: ({ value }) => formatCurrency(value) },
        {
            field: 'difference', headerName: 'Diferencia', type: 'number', width: 120,
            renderCell: (params) => (
                <Typography color={params.value < 0 ? 'error' : params.value > 0 ? 'success.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(params.value)}
                </Typography>
            )
        },
    ];

    const salesDetailsColumns = [
    { field: 'sale_date', headerName: 'Fecha', width: 180, type: 'dateTime', valueGetter: (value) => new Date(value) },
    { field: 'store_name', headerName: 'Tienda', width: 150 },
    { field: 'product_name', headerName: 'Producto', flex: 1, minWidth: 200 },
    { field: 'quantity', headerName: 'Cant.', type: 'number', width: 80 },
    { 
        field: 'purchase_price', 
        headerName: 'Costo Unit.', 
        type: 'number', 
        width: 120,
        // --- SINTAXIS CORREGIDA (SIMPLE) ---
        valueFormatter: (value) => formatCurrency(value) 
    },
    { 
        field: 'sale_price', 
        headerName: 'Precio Venta', 
        type: 'number', 
        width: 120,
        // --- SINTAXIS CORREGIDA (SIMPLE) ---
        valueFormatter: (value) => formatCurrency(value)
    },
    { 
        field: 'total_profit', 
        headerName: 'Utilidad Total', 
        type: 'number', 
        width: 140,
        // --- SINTAXIS CORREGIDA (SIMPLE) ---
        valueFormatter: (value) => formatCurrency(value)
    },
    {
        field: 'profit_margin',
        headerName: 'Margen',
        width: 120,
        sortable: false,
        renderCell: (params) => {
            // renderCell SÍ recibe el objeto 'params' completo, que contiene 'row'.
            const salePrice = Number(params.row.sale_price);
            const profit = Number(params.row.profit_per_unit);
            if (salePrice === 0) return <Chip label="N/A" size="small" />;
            const margin = (profit / salePrice) * 100;
            let color = 'success';
            if (margin <= 0) color = 'error';
            else if (margin < 5) color = 'warning';
            else if (margin < 10) color = 'info';
            return <Chip label={`${margin.toFixed(1)}%`} color={color} size="small" variant="outlined" />;
        }
    },
];

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Módulo de Reportes</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper sx={{ p: 2, mb: 4 }}>
                <Typography variant="h6">Filtros Generales</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={4}><TextField label="Fecha de Inicio" name="startDate" type="date" value={filters.startDate} onChange={handleFilterChange} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
                    <Grid item xs={12} sm={4}><TextField label="Fecha de Fin" name="endDate" type="date" value={filters.endDate} onChange={handleFilterChange} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
                    <Grid item xs={12} sm={4}><FormControl fullWidth>
                        <InputLabel>Tienda (Opcional)</InputLabel>
                        <Select name="storeId" value={filters.storeId} label="Tienda (Opcional)" onChange={handleFilterChange}>
                            <MenuItem value=""><em>Todas las tiendas</em></MenuItem>
                            {stores.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                        </Select>
                    </FormControl></Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 2, mb: 4 }}>
                <Typography variant="h6">Reporte de Rentabilidad por Producto Vendido</Typography>
                <Button sx={{my: 2}} variant="contained" onClick={generateSalesDetailsReport} disabled={loading.sales}>
                    {loading.sales ? <CircularProgress size={24} /> : 'Generar Reporte de Ventas'}
                </Button>
                {salesDetailsReport && (
                    <Box sx={{ height: 500, width: '100%' }}>
                        <DataGrid
                            rows={salesDetailsReport}
                            columns={salesDetailsColumns}
                            loading={loading.sales}
                            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                            getRowId={(row) => row.id} // Especificar el ID único de la fila
                        />
                    </Box>
                )}
            </Paper>

            <Paper sx={{ p: 2, mb: 4 }}>
                <Typography variant="h6">Reporte de Cortes de Caja</Typography>
                <Button sx={{my: 2}} variant="contained" onClick={generateCashSessionsReport} disabled={loading.cash}>
                    {loading.cash ? <CircularProgress size={24} /> : 'Generar Reporte de Cortes'}
                </Button>
                {cashSessionsReport && (
                    <Box sx={{ height: 400, width: '100%' }}>
                        <DataGrid
                            rows={cashSessionsReport}
                            columns={cashSessionsColumns}
                            loading={loading.cash}
                            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                        />
                    </Box>
                )}
            </Paper>

            <Paper sx={{ p: 2, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Reporte de Valor de Inventario</Typography>
                    <Button variant="contained" onClick={generateInventoryValueReport} disabled={loading.inventory}>
                        {loading.inventory ? <CircularProgress size={24} /> : 'Generar Reporte de Inventario'}
                    </Button>
                </Box>
                {inventoryValueReport && (
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Sucursal</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor Total del Inventario</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {inventoryValueReport.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{row.store_name}</TableCell>
                                        <TableCell align="right">{formatCurrency(row.total_purchase_value)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '& > *': { borderBottom: 'unset', fontWeight: 'bold', fontSize: '1.1rem' } }}>
                                    <TableCell>Total General</TableCell>
                                    <TableCell align="right">
                                        {formatCurrency(inventoryValueReport.reduce((sum, row) => sum + Number(row.total_purchase_value), 0))}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default ReportsPage;